import { create } from "zustand";
import { readAuthSession } from "@tensilelabs/auth-client";
import type { ConnectorBubble, TemplateVersion } from "./lib/types";
import { goldenVersion } from "./lib/fixtures";

export type BuilderRole = "owner" | "admin" | "run_only";

export interface BuilderDraftSnapshot {
  name: string;
  description: string;
  plan: TemplateVersion["plan"];
  dashboard: TemplateVersion["dashboard_schema"];
  flowchart: TemplateVersion["flowchart_view"];
  selectedConnectors: string[];
}

export interface BuilderState {
  role: BuilderRole;
  accountId: string;
  templateId: string;
  draftId?: string;
  versionId?: string;
  /** False when the builder service is unreachable (offline preview); chat is disabled then. */
  serviceAvailable: boolean;
  currentStep: number;
  name: string;
  description: string;
  selectedConnectors: string[];
  threadId?: string;
  draftRevision: number;
  dirty: boolean;
  statusMessage?: string;
  bubbles: ConnectorBubble[];
  plan: TemplateVersion["plan"];
  dashboard: TemplateVersion["dashboard_schema"];
  flowchart: TemplateVersion["flowchart_view"];
  setRole: (role: BuilderRole) => void;
  setStep: (step: number) => void;
  setName: (name: string) => void;
  setDescription: (description: string) => void;
  toggleConnector: (entryId: string) => void;
  setThread: (threadId: string) => void;
  markSaved: (revision: number) => void;
  setStatus: (message: string) => void;
  setBubbles: (bubbles: ConnectorBubble[]) => void;
  updatePlanLabel: (stepId: string, label: string) => void;
  hydrate: (payload: {
    name: string;
    description: string;
    plan: TemplateVersion["plan"];
    dashboard: TemplateVersion["dashboard_schema"];
    flowchart: TemplateVersion["flowchart_view"];
    connectors: string[];
    draftId: string;
    revision: number;
  }) => void;
  applySnapshot: (snapshot: BuilderDraftSnapshot) => void;
  setDraftId: (draftId: string) => void;
  setTemplateId: (templateId: string) => void;
  setVersionId: (versionId?: string) => void;
  setServiceAvailable: (available: boolean) => void;
}

const initialSession = readAuthSession();

export const useBuilderStore = create<BuilderState>((set) => ({
  role: initialSession?.role ?? "owner",
  accountId: initialSession?.accountId ?? "00000000-0000-0000-0000-000000000001",
  templateId: goldenVersion.template_id,
  currentStep: 1,
  name: "Quarterly filing",
  description: "File a quarterly return from the portal.",
  selectedConnectors: ["irs-portal"],
  serviceAvailable: true,
  draftRevision: 1,
  dirty: false,
  bubbles: [],
  plan: goldenVersion.plan,
  dashboard: goldenVersion.dashboard_schema,
  flowchart: goldenVersion.flowchart_view,
  setRole: (role) => set({ role }),
  setStep: (currentStep) => set({ currentStep }),
  setName: (name) => set({ name, dirty: true }),
  setDescription: (description) => set({ description, dirty: true }),
  toggleConnector: (entryId) =>
    set((state) => ({
      selectedConnectors: state.selectedConnectors.includes(entryId)
        ? state.selectedConnectors.filter((id) => id !== entryId)
        : [...state.selectedConnectors, entryId],
      dirty: true,
    })),
  setThread: (threadId) => set({ threadId }),
  markSaved: (draftRevision) => set({ draftRevision, dirty: false, statusMessage: "Draft saved" }),
  setStatus: (statusMessage) => set({ statusMessage }),
  setBubbles: (bubbles) => set({ bubbles }),
  hydrate: (payload) =>
    set({
      name: payload.name,
      description: payload.description,
      plan: payload.plan,
      dashboard: payload.dashboard,
      flowchart: payload.flowchart,
      selectedConnectors: payload.connectors,
      draftId: payload.draftId,
      draftRevision: payload.revision,
      dirty: false,
    }),
  applySnapshot: (snapshot) =>
    set({
      name: snapshot.name,
      description: snapshot.description,
      plan: snapshot.plan,
      dashboard: snapshot.dashboard,
      flowchart: snapshot.flowchart,
      selectedConnectors: snapshot.selectedConnectors,
      dirty: true,
    }),
  setDraftId: (draftId) => set({ draftId }),
  setTemplateId: (templateId) => set({ templateId }),
  setVersionId: (versionId) => set({ versionId }),
  setServiceAvailable: (serviceAvailable) => set({ serviceAvailable }),
  updatePlanLabel: (stepId, label) =>
    set((state) => ({
      flowchart: {
        nodes: [
          ...state.flowchart.nodes.filter((hint) => hint.step_id !== stepId),
          { step_id: stepId, label },
        ],
      },
      dirty: true,
    })),
}));
